import React, { useContext } from 'react'
import { Button, Modal, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import { Link, useRouteMatch } from 'react-router-dom'
import { adminUrl, adminCustomPageUrl } from 'config/constants/urls'
import CustomAppCreate from 'src/apps/CustomApp/components/Create'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import ModalRoute from 'src/components/utils/ModalRoute'
import { usePageList, useOrganizationWithOptions, useUpdateOrganization,
   useUpdatePage } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'

const ManagePages = () => {
   const match = useRouteMatch()
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const { organization, networkStatus } = useOrganizationWithOptions({
      withGlobalNavLinksTargetMetadata: true
   })

   const { updateOrganization, mutating } = useUpdateOrganization()
   const { updatePage } = useUpdatePage()
   const { pageList } = usePageList({
      archived: 'include',
      unpublished: 'include',
      limit: 50
   })

   const organizationInitialLoading = networkStatus <= 2
   if (organizationInitialLoading) {
      return null
   }

   const globalNavLinks = organization.globalNavLinks
      .filter(l => l.__typename !== 'NavExternalLinkElement')
      .filter(l => !l.app)

   const disabledPages = pageList.items.filter(p => !p.published)

   // TODO: Bring back when apps are real
   // PR #713
   // const disabledApps = organization.apps.filter(app => !app.enabled)

   const handleToggleCustomApp = async (page, enabled) => {
      try {
         await updatePage({ id: page.id }, { published: enabled })

         // TODO: For now, while pages are pretending to be custom apps,
         // We manually sync updates to globalNavLinks
         // PR #692
         const pageGlobalNavLink = organization.globalNavLinks
            .find(l => l.page?.id === page.id)

         // Now, manually create or delete a corresponding globalNavLink
         const operationName = enabled
            ? 'createInternalLink'
            : 'remove'

         const payload = enabled
            ? { name: page.title, pageId: page.id }
            : pageGlobalNavLink.id

         await updateOrganization({
            globalNavLinks: [{
               operation: operationName,
               [operationName]: payload
            }]
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   const handleToggleApp = async (appId, enabled) => {
      try {
         await updateOrganization({
            apps: [{
               operation: 'update',
               update: {
                  id: appId,
                  enabled
               }
            }]
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   return (
      <>
         <ProtoReturnBar
            display="block[a-d] none[e-f]"
            returnLinkName="Admin Workspace"
            returnLinkTo={adminUrl}
         />
         <View display="flex" justifyContent="center">
            <View
               width="100%"
               maxWidth="100%[a-c] 650px[d-f]"
               padding="40px 20px">
               <View
                  width="100%"
                  display="flex"
                  flexFlow="column[a-c] row[d-f]">
                  <View flex="1 1 auto" paddingRight="30px[b-f]">
                     <ProtoHeadingBlock
                        title="Create & Manage Custom Pages"
                        description="Enable and Disable which pages are available to your Community. Disabling a page will not delete its contents, but will make its content unavailable to Community members."
                     />
                  </View>
                  <View flex="0 0 auto" paddingTop="20px">
                     <Button
                        linkTo={`${match.url}/create`}
                        name="Create Custom Page"
                        color={theme.protoSettingsHighlightBackgroundColor}
                        textColor={theme.protoSettingsHighlightForegroundColor}
                        textWeight="medium"
                        size="md"
                        textSize="0.9"
                        padding="25px"
                     />
                  </View>
               </View>
               {mutating && (
                  <View marginBottom="12px">
                     <NotificationInline
                        borderRadius="6px"
                        type="loading"
                        title="Updating..."
                        message="Update in progress"
                        paddingHorizontal="12px"
                        paddingVertical="12px"
                        textSize="1.5"
                     />
                  </View>
               )}
               <View width="100%" padding="12px 0px">
                  <Spacer size="5" />
                  <Text size="2" weight="medium" color="#000">
                     Active Pages
                  </Text>
               </View>
               <View
                  width="100%"
                  borderRadius="3px">
                  <View width="100%">
                     <View
                        display="flex"
                        justifyContent="space-between"
                        padding="15px 20px"
                        backgroundColor="#fff"
                        borderTop="1px solid #e5e5e5">
                        <Text size="2" weight="medium" color="#000">
                           Home
                        </Text>
                        <Link to={`${adminUrl}/pages/home`}>
                           <Text size="1.5" color="#666">
                              Edit
                           </Text>
                        </Link>
                     </View>
                     <View
                        display="flex"
                        justifyContent="space-between"
                        padding="15px 20px"
                        backgroundColor="#fff"
                        borderTop="1px solid #e5e5e5">
                        <Text size="2" weight="medium" color="#000">
                           Explore
                        </Text>
                        <Link to={`${adminUrl}/pages/explore`}>
                           <Text size="1.5" color="#666">
                              Edit
                           </Text>
                        </Link>
                     </View>
                     {globalNavLinks.map(globalNavLink => (
                        <View
                           key={globalNavLink.id}
                           display="flex"
                           justifyContent="space-between"
                           padding="15px 20px"
                           backgroundColor="#fff"
                           borderTop="1px solid #e5e5e5">
                           <Text size="2" weight="medium" color="#000">
                              {globalNavLink.name}
                           </Text>
                           <View display="flex" alignItems="center">
                              <View
                                 onClick={() => {
                                    if (globalNavLink.app) {
                                       handleToggleApp(globalNavLink.app.id, false)
                                    } else {
                                       handleToggleCustomApp(globalNavLink.page, false)
                                    }
                                 }}>
                                 <Text size="1.5" color="#666">
                                    Disable
                                 </Text>
                              </View>
                              {!globalNavLink.app && (
                                 <View marginLeft="20px">
                                    <Link to={adminCustomPageUrl(globalNavLink.page)}>
                                       <Text size="1.5" color="#666">
                                          Edit
                                       </Text>
                                    </Link>
                                 </View>
                              )}
                           </View>
                        </View>
                     ))}
                     {disabledPages.length > 0 && (
                        <>
                           <View width="100%" padding="12px 0px">
                              <Spacer size="3" />
                              <Text size="2" weight="medium" color="#000">
                                 Disabled Pages
                              </Text>
                           </View>
                           {/*
                              TODO: Bring this back when apps are real again
                              PR #713
                              {disabledApps.map(app => (
                              <View
                                 key={app.id}
                                 display="flex"
                                 justifyContent="space-between"
                                 padding="15px 20px"
                                 backgroundColor="#fff"
                                 borderTop="1px solid #eee">
                                 <Text size="2" weight="medium" color="#888">
                                    {app.name}
                                 </Text>
                                 <View onClick={() => handleToggleApp(app.id, true)}>
                                    <Text size="1.5" color="#666">
                                       Enable
                                    </Text>
                                 </View>
                              </View>
                           ))} */}
                           {disabledPages.map(page => (
                              <View
                                 key={page.id}
                                 display="flex"
                                 justifyContent="space-between"
                                 padding="15px 20px"
                                 backgroundColor="#fff"
                                 borderTop="1px solid #eee">
                                 <Text size="2" weight="medium" color="#888">
                                    {page.title}
                                 </Text>
                                 <View display="flex" alignItems="center">
                                    <View onClick={() => handleToggleCustomApp(page, true)}>
                                       <Text size="1.5" color="#666">
                                          Enable
                                       </Text>
                                    </View>
                                    <View marginLeft="20px">
                                       <Link to={adminCustomPageUrl(page)}>
                                          <Text size="1.5" color="#666">
                                             Edit
                                          </Text>
                                       </Link>
                                    </View>
                                 </View>
                              </View>
                           ))}
                        </>
                     )}
                  </View>
               </View>
               <Spacer size="6" />
            </View>
         </View>
         <ModalRoute path={`${match.path}/create`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="3px[c-f]"
                  width="100%[a-b] 350px[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <CustomAppCreate
                     onCancelButtonClick={onCloseTrigger}
                     onCreate={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
      </>
   )
}

export default ManagePages
